/**
 * Vendor API wrapper — thin client over Railway /api/vendors. Same shape
 * and exported names as the old vendorService so call sites don't change.
 */

import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import {
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryPaginated,
  tryUpdate,
  tryUpdatePost,
  toQueryString,
} from "./_shared";

export type Vendor = Tables<"vendors">;
export type VendorInsert = Omit<TablesInsert<"vendors">, "company_id">;
export type VendorUpdate = TablesUpdate<"vendors">;

type VendorWithStatus = Vendor & {
  current_status: { id: string; name: string } | null;
  external_system: string | null;
};

type VendorWithAuditNames = Vendor & {
  created_by_profile: { full_name: string | null } | null;
  updated_by_profile: { full_name: string | null } | null;
};

export interface StatusChangeOptions {
  comments?: string;
  dueToReassignment?: boolean;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export const vendorService = {
  get(id: string) {
    return tryGet<Vendor>(`/api/vendors/${encodeURIComponent(id)}`);
  },

  getWithAuditNames(id: string) {
    return tryGet<VendorWithAuditNames>(
      `/api/vendors/${encodeURIComponent(id)}${toQueryString({ withAuditNames: true })}`,
    );
  },

  list(_companyId: string) {
    return tryList<Vendor>("/api/vendors");
  },

  listWithStatus(_companyId: string) {
    return tryList<VendorWithStatus>(`/api/vendors${toQueryString({ withStatus: true })}`);
  },

  listWithStatusPaginated(
    _companyId: string,
    pagination: PaginationParams,
    filters?: { search?: string; externalSystem?: string | null },
  ) {
    return tryPaginated<VendorWithStatus>(
      `/api/vendors${toQueryString({
        withStatus: true,
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters?.search,
        externalSystem: filters?.externalSystem ?? undefined,
      })}`,
    );
  },

  listActive(_companyId: string, activeStatusId?: string | null) {
    return tryList<Vendor>(
      `/api/vendors${toQueryString({ statusId: activeStatusId ?? undefined })}`,
    );
  },

  listAllWithStatus(_companyId: string) {
    return tryList<VendorWithStatus>(
      `/api/vendors${toQueryString({ withStatus: true, batchAll: true })}`,
    );
  },

  create(data: VendorInsert, _context: ServiceContext) {
    return tryCreate<Vendor>("/api/vendors", data);
  },

  // Snapshot is fetched server-side now; we keep it in the signature so
  // existing call sites compile without churn.
  update(
    id: string,
    data: VendorUpdate,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ) {
    return tryUpdate<Vendor>(`/api/vendors/${encodeURIComponent(id)}`, data);
  },

  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/vendors/${encodeURIComponent(id)}`);
  },

  updateStatus(
    id: string,
    newStatusId: string,
    previousStatusId: string | null,
    _context: ServiceContext,
    options?: StatusChangeOptions,
  ) {
    return tryUpdatePost<Vendor>(`/api/vendors/${encodeURIComponent(id)}/status`, {
      status_id: newStatusId,
      previous_status_id: previousStatusId,
      comments: options?.comments,
      due_to_reassignment: options?.dueToReassignment,
    });
  },
};
